import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DpMarketSelectorService } from '@app-modules/site-context/services/market-selector.service';
import { DpBaseStoreApiService } from '@app/core/services/api/base-store-api.service';
import { DpCurrentLocationService } from '@app/core/services/current-location.service';
import { DpGeoLocationApiService } from '@app/core/services/api/geolocation-api.service';

import {
  BaseSiteService,
  ConfigInitializerService,
  WindowRef,
  BaseSite,
  AuthService,
  User,
  Country
} from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import Cookies from 'js-cookie';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, find, switchMap, take, tap } from 'rxjs/operators';

import { DpGtmService } from '@app/core/services/gtm.service';

@Injectable({ providedIn: 'root' })
export class BaseSiteInitializer {
  readonly #destroyRef = inject(DestroyRef);
  readonly #marketFromUrl: string;
  readonly #langFromUrl: string;

  readonly #geolocationService = inject(DpGeoLocationApiService);
  readonly #currentLocationService = inject(DpCurrentLocationService);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #baseStoreService = inject(DpBaseStoreApiService);

  constructor(
    protected baseSiteService: BaseSiteService,
    protected windowRef: WindowRef,
    private readonly authService: AuthService,
    private readonly userService: UserAccountFacade,
    private readonly gtmService: DpGtmService,
    private readonly dpMarketSelectorService: DpMarketSelectorService,
    configInit: ConfigInitializerService
  ) {
    const previousUrl = (this.windowRef.location?.href ?? '').split('/');
    this.#marketFromUrl = previousUrl[3];
    this.#langFromUrl = previousUrl[4];
    this.#baseStoreService.fetchAndGetShopOpenStatus();

    configInit
      .getStable('context')
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        switchMap(() => this.dpMarketSelectorService.setContextFromCookie())
      )
      .subscribe();
  }

  initialize(): void {
    this.authService
      .isUserLoggedIn()
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        tap((isLoggedIn: boolean) => this.gtmService.handleGtmInit(isLoggedIn)),
        distinctUntilChanged(),
        switchMap((isLoggedIn) => (isLoggedIn ? this.handleLoggedInUserInit() : this.handleGuestInit()))
      )
      .subscribe();
  }

  handleGuestInit(): Observable<BaseSite | string | undefined> {
    if (this.#shouldDetectLocation() && isPlatformBrowser(this.#platformId)) {
      this.#initCountryUsingGeolocation();
    } else {
      const cookieMarket = Cookies.get('market') ?? 'uk-ie';
      const cookieCountry = Cookies.get('country') ?? 'uk-ie';
      const cookieLanguage = Cookies.get('language');
      const dataAreNotTheSame = this.#langFromUrl !== cookieLanguage || this.#marketFromUrl !== cookieMarket;
      if (this.#langFromUrl && this.#marketFromUrl && dataAreNotTheSame) {
        const newMarket = this.#marketFromUrl !== cookieMarket ? this.#marketFromUrl : cookieCountry;
        const newLang = this.#langFromUrl !== cookieLanguage ? this.#langFromUrl : cookieLanguage;
        return this.dpMarketSelectorService.setContextFromUrlOrCookie(newMarket, newLang);
      }
    }
    return this.baseSiteService.getActive();
  }

  handleLoggedInUserInit(): Observable<BaseSite | undefined> {
    return (this.userService.get() as Observable<User>).pipe(
      takeUntilDestroyed(this.#destroyRef),
      find((user) => !!user),
      switchMap((user: User | undefined) => {
        if (user) {
          return this.dpMarketSelectorService.setCurrent(
            user?.market,
            user?.country,
            user?.language?.isocode,
            user?.currency?.isocode
          );
        }

        return of(undefined);
      })
    );
  }

  #initCountryUsingGeolocation(): void {
    this.#currentLocationService
      .getCurrentLocation()
      .pipe(
        take(1),
        switchMap(({ coords }) => this.#getGeolocation(coords)),
        filter(Boolean),
        switchMap((country: Country) => this.dpMarketSelectorService.setContextGeolocation(country))
      )
      .subscribe();
  }

  #shouldDetectLocation(): boolean {
    return !(Cookies.get('market') || Cookies.get('country') || !(this.#langFromUrl && this.#marketFromUrl));
  }

  #getGeolocation({ latitude, longitude }: GeolocationCoordinates): Observable<Country> {
    return this.#geolocationService.getGeolocationData(latitude, longitude);
  }
}
